import { useImport } from "@/contexts/ImportContext";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, AlertTriangle, X, Sparkles } from "lucide-react";

export function ImportProgressBar() {
  const {
    isMinimized,
    currentIndex,
    totalProducts,
    successCount,
    errorCount,
    siteName,
    maximize,
    cancelImport,
    phase,
    embedding,
  } = useImport();

  if (!isMinimized) return null;

  const isComplete = phase === "done";
  const hasErrors = errorCount > 0 || embedding.failed > 0;
  const isEmbedding = phase === "embedding";

  const importPercent = totalProducts > 0
    ? ((successCount + errorCount) / totalProducts) * 100
    : 0;

  const embeddingPercent = embedding.total > 0
    ? ((embedding.done + embedding.failed) / embedding.total) * 100
    : embedding.percent;

  const progressPercent = isEmbedding ? embeddingPercent : importPercent;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 
                 bg-card border shadow-lg rounded-xl p-4 
                 min-w-[340px] max-w-[90vw] sm:max-w-md
                 animate-in slide-in-from-bottom-4 duration-300"
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-full ${
          isComplete
            ? hasErrors
              ? "bg-yellow-500/10 text-yellow-500"
              : "bg-green-500/10 text-green-500"
            : isEmbedding
              ? "bg-accent/20 text-accent-foreground"
              : "bg-primary/10 text-primary"
        }`}>
          {isComplete ? (
            hasErrors ? <AlertTriangle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />
          ) : isEmbedding ? (
            <Sparkles className="w-5 h-5 animate-pulse" />
          ) : (
            <Loader2 className="w-5 h-5 animate-spin" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium truncate">
              {isComplete
                ? `Importação de ${siteName} concluída`
                : isEmbedding
                  ? `Indexando produtos de ${siteName}...`
                  : `Importando de ${siteName}...`}
            </p>
          </div>

          {isComplete ? (
            <p className="text-xs text-muted-foreground">
              {successCount} importado(s){hasErrors && `, ${errorCount + embedding.failed} erro(s)`}
            </p>
          ) : (
            <>
              <Progress value={progressPercent} className="h-1.5 mb-1" />
              <p className="text-xs text-muted-foreground">
                {isEmbedding
                  ? `${embedding.done + embedding.failed} de ${embedding.total} indexados`
                  : `${currentIndex + 1} de ${totalProducts} produtos`}
              </p>
            </>
          )}
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={maximize}
            className="h-8 px-3"
          >
            Abrir
          </Button>
          {!isComplete && (
            <Button
              variant="ghost"
              size="icon"
              onClick={cancelImport}
              className="h-8 w-8 text-muted-foreground hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
